import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merge Tailwind classes intelligently.
 * Usage: mergeStyles("p-2", condition && "bg-[#2bb6f7]")
 */
export function mergeStyles(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const checkUserStatus = (status: string) => {
  if (status === "active") {
      return "Active";
  } else if (status === "blocked") {
      return "Blocked";
  } else if (status === "inactive") {
      return "Inactive";
  }
  return "Inactive";
}

export const checkUserRole = (role: string) => {
  if (role === "MANAGER") {
    return "Manager";
  } else if (role === "ADMIN") {
    return "Admin";
  } else if (role === "PE") {
    return "PE";
  }
  return "PE";
}

export const backendVendorToFrontendVendor = (vendor: string) => {
  switch (vendor) {
    case "btBlue":
      return "BT Blue";
    case "iata":
      return "IATA";
    case "cargo7":
      return "Cargo 7";
    case "vendor1":
      return "Vendor 1";
    case "vendor2":
      return "Vendor 2";
    case "vendor3":
      return "Vendor 3";
    case "vendor4":
      return "Vendor 4";
    case "vendor5":
      return "Vendor 5";
  }
  return vendor;
}

export const generateInitialAvatar = (name: string, size = 128) => {
  const initial = (name || 'U').trim().charAt(0).toUpperCase() || 'U';
  const backgroundColor = '#f3f4f6';
  const textColor = '#111827';
  const fontSize = Math.floor(size * 0.5);
  const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="100%" height="100%" fill="${backgroundColor}"/><text x="50%" y="50%" dy="0.36em" text-anchor="middle" font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, Noto Sans, " font-size="${fontSize}" fill="${textColor}">${initial}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Basic HTML sanitizer for rendering limited email content safely
export function sanitizeHtml(input: string): string {
  if (!input) return '';

  // Remove escaped HTML comments (e.g. &lt;!-- ... --&gt;)
  input = input.replace(/&lt;!--[\s\S]*?--&gt;/gi, '');

  const allowedTags = new Set([
    'b','strong','i','em','u','br','p','div','span','ul','ol','li','a',
    'table','thead','tbody','tr','th','td'
  ]);
  const allowedAttrs: Record<string, Set<string>> = {
    a: new Set(['href','target','rel'])
  };

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(input), 'text/html');

  const isSafeHref = (href: string): boolean => {
    try {
      const val = href.trim().toLowerCase();
      return val.startsWith('http://') || val.startsWith('https://') || val.startsWith('mailto:') || val.startsWith('tel:');
    } catch { return false; }
  };

  const walk = (node: Node) => {
    const parent = node.parentNode as HTMLElement | null;
    if (!parent) return;

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      // Hard block dangerous or useless tags
      if (tag === 'style' || tag === 'script') {
        parent.removeChild(el);
        return;
      }

      if (!allowedTags.has(tag)) {
        // Replace disallowed element with its text content
        const text = doc.createTextNode(el.textContent || '');
        parent.replaceChild(text, el);
        return;
      }

      // Scrub attributes
      const allowedForTag = allowedAttrs[tag] || new Set<string>();
      Array.from(el.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if (!allowedForTag.has(name)) {
          el.removeAttribute(attr.name);
          return;
        }
        // Special handling for anchors
        if (tag === 'a' && name === 'href') {
          const href = attr.value || '';
          if (!isSafeHref(href)) {
            el.removeAttribute('href');
          } else {
            el.setAttribute('target', '_blank');
            el.setAttribute('rel', 'noopener noreferrer');
          }
        }
      });

      // Recurse children (clone array since we may modify)
      Array.from(el.childNodes).forEach(walk);
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
    
      // Remove leftover comment patterns inside text
      if (/<!--|-->/g.test(text)) {
        parent.removeChild(node);
      }
    }    
    if (node.nodeType === Node.COMMENT_NODE) {
      parent.removeChild(node);
      return;
    }
  };

  Array.from(doc.body.childNodes).forEach(walk);
  return doc.body.innerHTML;
}

const parseDateInput = (dateInput: string): Date | null => {
  const raw = String(dateInput ?? '').trim();
  if (!raw) return null;

  // Handle DD/MM/YYYY or DD-MM-YYYY explicitly (backend sometimes sends 17/03/2026)
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s|T|$)/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (
      Number.isFinite(day) &&
      Number.isFinite(month) &&
      Number.isFinite(year) &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12
    ) {
      // Build a local Date to avoid timezone surprises from "YYYY-MM-DD" parsing behavior
      const dt = new Date(year, month - 1, day);
      return isNaN(dt.getTime()) ? null : dt;
    }
  }

  const dt = new Date(raw);
  return isNaN(dt.getTime()) ? null : dt;
};

// Format date only as YYYY-MM-DD (no time)
export const formatDate = (dateString: string | null | undefined): string => {
  if (dateString == null || String(dateString).trim() === "") return "-";
  const date = parseDateInput(String(dateString));
  if (!date) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

  // Format date with time as YYYY-MM-DD HH:mm:ss (24hr, local time)
export const formatDateTime = (dateString: string | null | undefined): string => {
    if (dateString == null || String(dateString).trim() === '') return '-';
    try {
      const date = parseDateInput(String(dateString));
      if (!date) return '-';
      const pad = (n: number) => String(n).padStart(2, '0');
      const y = date.getFullYear();
      const m = pad(date.getMonth() + 1);
      const d = pad(date.getDate());
      const hh = pad(date.getHours());
      const mm = pad(date.getMinutes());
      const ss = pad(date.getSeconds());
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    } catch {
      return '-';
    }
  };
